import { useRef } from 'react';

type UseFrozenValidationIdsParams = {
  /** 검증 기준으로 사용할 id 목록 */
  ids: string[];
  /** 마운트 이후 실제 fetch가 완료됐는지 (react-query의 `isFetchedAfterMount`) */
  isFetchedAfterMount: boolean;
};

/**
 * 중복 검증 기준 id 목록을 마운트 이후 최초 fetch 완료 시점에 한 번만 고정한다.
 * @description
 * - 폼이 열려 있는 동안 기준이 흔들려 이미 선택한 값이 갑자기 오류가 되는 것을 막는다.
 * - `data !== undefined`가 아니라 `isFetchedAfterMount`로 판정한다. invalidate로 stale 표시된
 *   캐시가 첫 렌더에 즉시 반환되는데, 그 값을 고정하면 백그라운드 refetch 결과가 반영되지 않는다.
 * - 고정 전에는 현재 `ids`를 그대로 사용한다. stale 값으로 과하게 막을 수는 있어도 중복 제출을
 *   통과시키지는 않는 방향이다.
 * @example
 * const { data, isFetchedAfterMount } = useGetProjectEffectiveUsers({ ... });
 * const existUserIds = useMemo(() => extractDefinedValues(data?.users, 'id'), [data?.users]);
 * const validationUserIds = useFrozenValidationIds({ ids: existUserIds, isFetchedAfterMount });
 */
const useFrozenValidationIds = (params: UseFrozenValidationIdsParams): string[] => {
  const { ids, isFetchedAfterMount } = params;

  const frozenIdsRef = useRef(ids);
  const isFrozenRef = useRef(false);

  if (!isFrozenRef.current && isFetchedAfterMount) {
    isFrozenRef.current = true;
    frozenIdsRef.current = ids;
  }

  return isFrozenRef.current ? frozenIdsRef.current : ids;
};

export type { UseFrozenValidationIdsParams };

export default useFrozenValidationIds;
